import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import * as XLSX from "xlsx";
import { useSelector } from "react-redux";
import "./Bestsell.css";

function BestSell() {
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [selectedLocation, setSelectedLocation] = useState("");
  const [selectedSubProduct, setSelectedSubProduct] = useState("");
  const [availableLocations, setAvailableLocations] = useState([]);
  const [subProducts, setSubProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingLocations, setLoadingLocations] = useState(false);
  const [loadingSubProducts, setLoadingSubProducts] = useState(false);
  const [salesData, setSalesData] = useState([]);
  const [allBills, setAllBills] = useState([]);
  const [productsWithCategories, setProductsWithCategories] = useState({});
  const [allProducts, setAllProducts] = useState([]); // NOUVEAU: Stocker tous les produits
  
  const [sortType, setSortType] = useState("revenue");
  const [sortDirection, setSortDirection] = useState("desc");
  
  const [stats, setStats] = useState({
    totalSales: 0,
    totalRevenue: 0,
    totalProductsSold: 0,
    averageSaleValue: 0,
    topProduct: null,
    topCategory: null,
    topSubProduct: null,
    filteredProducts: [],
    filteredCategories: [],
    filteredSubProducts: []
  });
  
  const currentUser = useSelector((state) => state.AuthReducer?.user);
  
  const extractLocId = (loc) => {
    if (!loc) return null;
    
    if (typeof loc === "string") return loc;
    
    if (loc.$oid) return loc.$oid;
    
    if (loc._id) {
      if (typeof loc._id === 'string') return loc._id;
      if (loc._id.$oid) return loc._id.$oid;
      return loc._id.toString();
    }
    
    return loc.toString ? loc.toString() : String(loc);
  };

  // Charger les localisations disponibles
  useEffect(() => {
    fetchAvailableLocations();
    fetchSubProducts();
    fetchAllProducts(); // NOUVEAU: Charger tous les produits
  }, []);

  // NOUVEAU: Charger tous les produits avec leurs catégories
  const fetchAllProducts = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get("/api/Product/getProduct", {
        headers: { Authorised: token }
      });
      
      if (response.data && Array.isArray(response.data)) {
        setAllProducts(response.data);
        console.log("✅ Produits chargés:", response.data.length);
      }
    } catch (error) {
      console.error("Erreur chargement produits:", error);
    }
  };

  // Charger les sous-produits avec leurs catégories - VERSION AMÉLIORÉE
  const fetchSubProducts = async () => {
    setLoadingSubProducts(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get("/api/Product/getAllSubProduct", {
        headers: { Authorised: token }
      });
      
      if (response.data && Array.isArray(response.data)) {
        setSubProducts(response.data);
        
        // Créer un mapping complet nom du sous-produit -> catégorie
        const categoryMap = {};
        response.data.forEach(subProduct => {
          if (subProduct.name && subProduct.product) {
            // Utiliser le nom du sous-produit comme clé
            categoryMap[subProduct.name] = subProduct.product.category || "FOOD";
          }
        });
        
        // Ajouter aussi les produits parents dans le mapping
        allProducts.forEach(product => {
          if (product.name && product.category) {
            categoryMap[product.name] = product.category;
          }
        });
        
        setProductsWithCategories(categoryMap);
        console.log("✅ Mapping catégories créé:", Object.keys(categoryMap).length, "entrées");
      }
    } catch (error) {
      console.error("Erreur chargement sous-produits:", error);
    } finally {
      setLoadingSubProducts(false);
    }
  };

  const fetchAvailableLocations = async () => {
    setLoadingLocations(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get("/api/Locationspin/getAllLocations", {
        headers: { Authorised: token }
      });
      
      if (response.data && Array.isArray(response.data)) {
        setAvailableLocations(response.data);
        
        if (currentUser?.role !== "admin" && currentUser?.localisation) {
          const userLocationId = extractLocId(currentUser.localisation);
          const userLoc = response.data.find(loc => 
            extractLocId(loc) === userLocationId
          );
          if (userLoc) {
            setSelectedLocation(userLocationId);
          }
        }
      }
    } catch (error) {
      console.error("Erreur chargement localisations:", error);
    } finally {
      setLoadingLocations(false);
    }
  };

  // Charger toutes les données initiales
  useEffect(() => {
    fetchAllSalesData();
  }, []);

  const fetchAllSalesData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get("/api/Bills/getAllBills", {
        headers: { Authorised: token }
      });
      
      setAllBills(response.data || []);
      
      // Appliquer les filtres initiaux
      applyFilters(response.data || []);
      
    } catch (error) {
      console.error("Erreur lors du chargement des ventes:", error);
      setAllBills([]);
      processSalesData([]);
    } finally {
      setLoading(false);
    }
  };

  // CORRIGÉ: Fonction pour obtenir la catégorie depuis le mapping
  const getCategoryFromProductName = (productName) => {
    if (!productName) return "FOOD";
    
    // Chercher dans le mapping des sous-produits
    if (productsWithCategories[productName]) {
      return productsWithCategories[productName];
    }
    
    // Chercher dans les produits
    const foundProduct = allProducts.find(p => p.name === productName);
    if (foundProduct && foundProduct.category) {
      return foundProduct.category;
    }
    
    // Fallback: essayer de déterminer par le nom
    const name = productName.toLowerCase().trim();
    
    // DEALS d'abord (plus spécifique)
    if (name.includes("menu") || name.includes("combo") || name.includes("deal") || 
        name.includes("offre") || name.includes("formule") || name.includes("pack")) {
      return "DEALS";
    }
    
    // BEVERAGES
    if (name.includes("boisson") || name.includes("soda") || name.includes("coca") || 
        name.includes("jus") || name.includes("eau") || name.includes("thé") || 
        name.includes("café") || name.includes("milkshake") || name.includes("drink")) {
      return "BEVERAGES";
    }
    
    // Par défaut FOOD
    return "FOOD";
  };

  // CORRIGÉ: Fonction de normalisation de date
  const normalizeDate = (dateString) => {
    if (!dateString) return null;
    
    const date = new Date(dateString);
    date.setHours(0, 0, 0, 0);
    return date;
  };

  const applyFilters = (bills) => {
    let filteredBills = [...bills];
    
    // 1. CORRIGÉ: Filtrer par période
    if (startDate || endDate) {
      filteredBills = filteredBills.filter(bill => {
        const billDate = normalizeDate(bill.date || bill.createdAt);
        if (!billDate) return false;
        
        let startDateCheck = true;
        let endDateCheck = true;
        
        if (startDate) {
          const start = normalizeDate(startDate);
          startDateCheck = billDate >= start;
        }
        
        if (endDate) {
          const end = normalizeDate(endDate);
          endDateCheck = billDate <= end;
        }
        
        return startDateCheck && endDateCheck;
      });
    }
    
    // 2. Filtrer par localisation
    if (selectedLocation) {
      filteredBills = filteredBills.filter(bill => {
        const billLocationId = extractLocId(bill.localisation);
        return billLocationId === selectedLocation;
      });
    } else if (currentUser?.role !== "admin" && currentUser?.localisation) {
      const userLocationId = extractLocId(currentUser.localisation);
      filteredBills = filteredBills.filter(bill => {
        const billLocationId = extractLocId(bill.localisation);
        return billLocationId === userLocationId;
      });
    }
    
    // 3. CORRIGÉ: Filtrer par sous-produit
    if (selectedSubProduct) {
      filteredBills = filteredBills.filter(bill => {
        if (!bill.items || !Array.isArray(bill.items)) return false;
        
        return bill.items.some(item => {
          // Vérifier si l'item correspond au sous-produit sélectionné
          if (item.name === selectedSubProduct) return true;
          
          // Vérifier dans les subProducts de l'item
          if (item.subProducts && Array.isArray(item.subProducts)) {
            return item.subProducts.some(sub => sub.name === selectedSubProduct);
          }
          
          return false;
        });
      });
    }
    
    setSalesData(filteredBills);
    processSalesData(filteredBills);
  };

  const handleSearch = () => {
    applyFilters(allBills);
  };

  const resetFilters = () => {
    setStartDate("");
    setEndDate("");
    setSelectedLocation(currentUser?.role !== "admin" && currentUser?.localisation ? 
      extractLocId(currentUser.localisation) : "");
    setSelectedSubProduct("");
    
    if (currentUser?.role !== "admin" && currentUser?.localisation) {
      const userLocationId = extractLocId(currentUser.localisation);
      const filteredBills = allBills.filter(bill => {
        const billLocationId = extractLocId(bill.localisation);
        return billLocationId === userLocationId;
      });
      setSalesData(filteredBills);
      processSalesData(filteredBills);
    } else {
      setSalesData(allBills);
      processSalesData(allBills);
    }
  };

  // Fonction de tri
  const sortData = (array, type, direction) => {
    if (!array || array.length === 0) return array;
    
    const sortedArray = [...array];
    
    sortedArray.sort((a, b) => {
      let valueA, valueB;
      
      switch(type) {
        case "quantity":
          valueA = a.quantity || 0;
          valueB = b.quantity || 0;
          break;
          
        case "billsCount":
          valueA = a.billsCount || 0;
          valueB = b.billsCount || 0;
          break;
          
        case "averagePrice":
          valueA = a.averagePrice || 0;
          valueB = b.averagePrice || 0;
          break;
          
        case "name":
          valueA = a.name?.toLowerCase() || "";
          valueB = b.name?.toLowerCase() || "";
          break;
          
        case "category":
          // CORRIGÉ: Utiliser la catégorie déterminée par getCategoryFromProductName
          valueA = a.category || getCategoryFromProductName(a.name) || "FOOD";
          valueB = b.category || getCategoryFromProductName(b.name) || "FOOD";
          break;
          
        case "revenue":
        default:
          valueA = a.revenue || 0;
          valueB = b.revenue || 0;
          break;
      }
      
      if (type === "category" && valueA === valueB) {
        valueA = a.name?.toLowerCase() || "";
        valueB = b.name?.toLowerCase() || "";
      }
      
      if (direction === "asc") {
        if (typeof valueA === "string") {
          return valueA.localeCompare(valueB);
        }
        return valueA - valueB;
      } else {
        if (typeof valueA === "string") {
          return valueB.localeCompare(valueA);
        }
        return valueB - valueA;
      }
    });
    
    return sortedArray;
  };

  // CORRIGÉ: Fonction principale pour traiter les données de vente
  const processSalesData = (bills) => {
    if (!bills || bills.length === 0) {
      setStats({
        totalSales: 0,
        totalRevenue: 0,
        totalProductsSold: 0,
        averageSaleValue: 0,
        topProduct: null,
        topCategory: null,
        topSubProduct: null,
        filteredProducts: [],
        filteredCategories: [],
        filteredSubProducts: []
      });
      return;
    }

    const productsMap = new Map(); // Pour les produits principaux
    const categoriesMap = new Map(); // Pour les catégories
    const subProductsMap = new Map(); // Pour les sous-produits (unique par nom)
    let totalRevenue = 0;
    let totalProductsSold = 0;

    bills.forEach(bill => {
      if (bill.items && Array.isArray(bill.items)) {
        bill.items.forEach(item => {
          if (item.isCancelled) return;

          const quantity = item.quantity || 1;
          const price = item.price || 0;
          const itemTotal = price * quantity;
          
          totalRevenue += itemTotal;
          totalProductsSold += quantity;

          // CORRIGÉ: Déterminer la catégorie pour ce produit
          const productName = item.name || "Produit inconnu";
          const productCategory = getCategoryFromProductName(productName);
          
          // 1. Traiter le produit principal
          if (!productsMap.has(productName)) {
            productsMap.set(productName, {
              name: productName,
              quantity: 0,
              revenue: 0,
              bills: new Set(),
              category: productCategory
            });
          }
          const productData = productsMap.get(productName);
          productData.quantity += quantity;
          productData.revenue += itemTotal;
          productData.bills.add(bill.billNumber);

          // 2. Mettre à jour la catégorie
          if (!categoriesMap.has(productCategory)) {
            categoriesMap.set(productCategory, {
              name: productCategory,
              quantity: 0,
              revenue: 0,
              products: new Set()
            });
          }
          const categoryData = categoriesMap.get(productCategory);
          categoryData.quantity += quantity;
          categoryData.revenue += itemTotal;
          categoryData.products.add(productName);

          // 3. Traiter les sous-produits SI ils existent
          if (item.subProducts && Array.isArray(item.subProducts)) {
            item.subProducts.forEach(subProduct => {
              const subProductName = subProduct.name || "Sous-produit inconnu";
              
              // CORRIGÉ: La catégorie du sous-produit vient du produit parent
              // Pas besoin de recalculer, on utilise la même catégorie
              
              if (!subProductsMap.has(subProductName)) {
                subProductsMap.set(subProductName, {
                  name: subProductName,
                  quantity: 0,
                  revenue: 0,
                  parentProduct: productName,
                  category: productCategory // Utiliser la catégorie du parent
                });
              }
              const subProductData = subProductsMap.get(subProductName);
              subProductData.quantity += quantity;
              // Utiliser le prix du sous-produit s'il existe, sinon une partie du prix principal
              const subPrice = subProduct.price || (price / Math.max(item.subProducts.length, 1));
              subProductData.revenue += subPrice * quantity;
            });
          }
          
          // CORRIGÉ: NE PAS ajouter le produit principal comme sous-produit
          // pour éviter le double comptage
        });
      }
    });

    // Convertir en tableaux
    const productsArray = Array.from(productsMap.values()).map(data => ({
      ...data,
      billsCount: data.bills.size,
      averagePrice: data.quantity > 0 ? data.revenue / data.quantity : 0
    }));

    const categoriesArray = Array.from(categoriesMap.values()).map(data => ({
      ...data,
      productsCount: data.products.size,
      averagePrice: data.quantity > 0 ? data.revenue / data.quantity : 0
    }));

    const subProductsArray = Array.from(subProductsMap.values()).map(data => ({
      ...data,
      averagePrice: data.quantity > 0 ? data.revenue / data.quantity : 0
    }));

    // Trier les données
    const sortedProducts = sortData(productsArray, sortType, sortDirection);
    const sortedCategories = sortData(categoriesArray, "revenue", "desc");
    const sortedSubProducts = sortData(subProductsArray, sortType, sortDirection);

    // Statistiques globales
    const totalSales = bills.length;
    const averageSaleValue = totalSales > 0 ? totalRevenue / totalSales : 0;

    setStats({
      totalSales,
      totalRevenue,
      totalProductsSold,
      averageSaleValue,
      topProduct: sortedProducts.length > 0 ? sortedProducts[0] : null,
      topCategory: sortedCategories.length > 0 ? sortedCategories[0] : null,
      topSubProduct: sortedSubProducts.length > 0 ? sortedSubProducts[0] : null,
      filteredProducts: sortedProducts,
      filteredCategories: sortedCategories,
      filteredSubProducts: sortedSubProducts
    });
  };

  const getLocationName = (locationId) => {
    if (!locationId) return "Toutes";
    const location = availableLocations.find(loc => extractLocId(loc) === locationId);
    return location ? (location.name || location._id || "Inconnue") : "Inconnue";
  };

  // Fonctions de tri
  const handleSortChange = (type) => {
    setSortType(type);
    
    const sortedProducts = sortData(stats.filteredProducts, type, sortDirection);
    const sortedCategories = sortData(stats.filteredCategories, "revenue", "desc");
    const sortedSubProducts = sortData(stats.filteredSubProducts, type, sortDirection);
    
    setStats(prev => ({
      ...prev,
      filteredProducts: sortedProducts,
      filteredCategories: sortedCategories,
      filteredSubProducts: sortedSubProducts
    }));
  };

  const handleDirectionChange = (direction) => {
    setSortDirection(direction);
    
    const sortedProducts = sortData(stats.filteredProducts, sortType, direction);
    const sortedCategories = sortData(stats.filteredCategories, "revenue", "desc");
    const sortedSubProducts = sortData(stats.filteredSubProducts, sortType, direction);
    
    setStats(prev => ({
      ...prev,
      filteredProducts: sortedProducts,
      filteredCategories: sortedCategories,
      filteredSubProducts: sortedSubProducts
    }));
  };

  // Export Excel
  const exportToExcel = () => {
    if (!stats.filteredProducts || stats.filteredProducts.length === 0) {
      alert("Aucune donnée à exporter !");
      return;
    }

    const wb = XLSX.utils.book_new();
    const dateStr = new Date().toISOString().split('T')[0];
    
    // Feuille de résumé
    const summaryData = [
      ["RAPPORT DES MEILLEURES VENTES"],
      [`Période: ${startDate || "Début"} à ${endDate || "Aujourd'hui"}`],
      [`Localisation: ${getLocationName(selectedLocation)}`],
      [`Sous-produit filtré: ${selectedSubProduct || "Tous"}`],
      [`Tri appliqué: ${getSortTypeLabel(sortType)} (${sortDirection === "desc" ? "Descendant" : "Ascendant"})`],
      [`Généré le: ${new Date().toLocaleString()}`],
      [],
      ["STATISTIQUES GLOBALES"],
      ["Nombre total de ventes", stats.totalSales],
      ["Produits vendus (unités)", stats.totalProductsSold],
      ["Chiffre d'affaires total", `${stats.totalRevenue.toFixed(2)} DT`],
      ["Valeur moyenne par vente", `${stats.averageSaleValue.toFixed(2)} DT`],
      [],
      ["MEILLEURS PERFORMANTS"],
      ["Produit le plus vendu", stats.topProduct ? `${stats.topProduct.name} (${stats.topProduct.quantity} unités)` : "N/A"],
      ["Catégorie la plus vendue", stats.topCategory ? `${stats.topCategory.name} (${stats.topCategory.revenue.toFixed(2)} DT)` : "N/A"],
      ["Sous-produit le plus vendu", stats.topSubProduct ? `${stats.topSubProduct.name} (${stats.topSubProduct.quantity} unités)` : "N/A"]
    ];

    const wsSummary = XLSX.utils.aoa_to_sheet(summaryData);
    XLSX.utils.book_append_sheet(wb, wsSummary, "Résumé");

    // Feuille produits
    const productsData = [
      ["Rang", "Produit", "Catégorie", "Quantité", "Chiffre d'Affaires (DT)", "Factures", "Prix Moyen (DT)", "Part du CA (%)"]
    ];
    
    stats.filteredProducts.forEach((product, index) => {
      const marketShare = stats.totalRevenue > 0 ? (product.revenue / stats.totalRevenue) * 100 : 0;
      const category = product.category || getCategoryFromProductName(product.name);
      productsData.push([
        index + 1,
        product.name,
        category,
        product.quantity,
        product.revenue.toFixed(2),
        product.billsCount,
        product.averagePrice.toFixed(2),
        marketShare.toFixed(1)
      ]);
    });

    const wsProducts = XLSX.utils.aoa_to_sheet(productsData);
    XLSX.utils.book_append_sheet(wb, wsProducts, "Produits");

    // Feuille catégories
    const categoriesData = [
      ["Rang", "Catégorie", "Quantité", "Chiffre d'Affaires (DT)", "Produits", "Prix Moyen (DT)", "Part du CA (%)"]
    ];
    
    stats.filteredCategories.forEach((category, index) => {
      const marketShare = stats.totalRevenue > 0 ? (category.revenue / stats.totalRevenue) * 100 : 0;
      categoriesData.push([
        index + 1,
        category.name,
        category.quantity,
        category.revenue.toFixed(2),
        category.productsCount,
        category.averagePrice.toFixed(2),
        marketShare.toFixed(1)
      ]);
    });

    const wsCategories = XLSX.utils.aoa_to_sheet(categoriesData);
    XLSX.utils.book_append_sheet(wb, wsCategories, "Catégories");

    // Feuille sous-produits
    const subProductsData = [
      ["Rang", "Sous-produit", "Produit Parent", "Catégorie", "Quantité", "Chiffre d'Affaires (DT)", "Prix Moyen (DT)"]
    ];
    
    stats.filteredSubProducts.forEach((subProduct, index) => {
      const category = subProduct.category || getCategoryFromProductName(subProduct.name);
      subProductsData.push([
        index + 1,
        subProduct.name,
        subProduct.parentProduct,
        category,
        subProduct.quantity,
        subProduct.revenue.toFixed(2),
        subProduct.averagePrice.toFixed(2)
      ]);
    });

    const wsSubProducts = XLSX.utils.aoa_to_sheet(subProductsData);
    XLSX.utils.book_append_sheet(wb, wsSubProducts, "Sous-produits");

    // Exporter
    const fileName = `top_ventes_${dateStr}_${getLocationName(selectedLocation).replace(/\s+/g, '_')}.xlsx`;
    XLSX.writeFile(wb, fileName);
  };

  const getSortTypeLabel = (type) => {
    switch(type) {
      case "revenue": return "Chiffre d'affaires";
      case "quantity": return "Quantité vendue";
      case "billsCount": return "Nombre de factures";
      case "averagePrice": return "Prix moyen";
      case "name": return "Nom produit";
      case "category": return "Catégorie";
      default: return "Chiffre d'affaires";
    }
  };

  // CORRIGÉ: Couleurs pour les 3 catégories seulement
  const getCategoryColor = (category) => {
    switch(category) {
      case "FOOD": return { bg: '#e8f5e9', border: '#4caf50', text: '#2e7d32', badge: '#4caf50' };
      case "BEVERAGES": return { bg: '#e3f2fd', border: '#2196f3', text: '#1565c0', badge: '#2196f3' };
      case "DEALS": return { bg: '#fff3e0', border: '#ff9800', text: '#ef6c00', badge: '#ff9800' };
      default: return { bg: '#f5f5f5', border: '#9e9e9e', text: '#616161', badge: '#607d8b' };
    }
  };

  const renderLocationInfo = () => {
    if (!currentUser) return null;
    
    let locationText = "";
    if (currentUser.role === "admin") {
      locationText = "📋 VUE ADMIN - TOUTES LES LOCALISATIONS";
    } else if (currentUser.localisation) {
      const locName = currentUser.localisation.name || currentUser.localisation;
      locationText = `📍 LOCALISATION: ${locName}`;
    } else {
      locationText = "⚠️ LOCALISATION NON DÉFINIE";
    }
    
    return (
      <div className="location-info" style={{
        backgroundColor: currentUser.role === "admin" ? '#4caf50' : '#2196f3',
        color: 'white',
        padding: '10px',
        borderRadius: '5px',
        marginBottom: '20px',
        textAlign: 'center',
        fontWeight: 'bold'
      }}>
        {locationText}
        {salesData.length > 0 && (
          <div style={{ fontSize: '14px', fontWeight: 'normal', marginTop: '5px' }}>
            {salesData.length} vente(s) analysée(s) | Période: {startDate || "Début"} à {endDate || "Aujourd'hui"}
            {selectedSubProduct && ` | Sous-produit: ${selectedSubProduct}`}
          </div>
        )}
      </div>
    );
  };

  // CSS réorganisé pour Options de Classement et Filtres Avancés
  return (
    <div className="bestsell-container">
      <h1>📊 Dashboard des Meilleures Ventes</h1>
      
      {renderLocationInfo()}

      {/* Section unifiée pour Options de Classement et Filtres Avancés */}
      <div className="filter-sorting-section" style={{
        backgroundColor: '#f8f9fa',
        padding: '25px',
        borderRadius: '12px',
        marginBottom: '30px',
        border: '1px solid #dee2e6',
        boxShadow: '0 3px 10px rgba(0,0,0,0.08)'
      }}>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'flex-start',
          marginBottom: '25px',
          flexWrap: 'wrap',
          gap: '20px'
        }}>
          {/* Options de Classement */}
          <div className="sorting-options" style={{ 
            flex: '1',
            minWidth: '300px',
            backgroundColor: 'white',
            padding: '20px',
            borderRadius: '8px',
            border: '1px solid #e0e0e0'
          }}>
            <h3 style={{ 
              color: '#495057', 
              marginBottom: '20px', 
              paddingBottom: '10px',
              borderBottom: '2px solid #007bff'
            }}>
              ⚙️ Options de Classement
            </h3>
            
            <div style={{ marginBottom: '20px' }}>
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center', 
                marginBottom: '15px' 
              }}>
                <h4 style={{ margin: 0, color: '#6c757d', fontSize: '15px' }}>Type de classement:</h4>
                <div style={{ 
                  backgroundColor: '#e9ecef', 
                  padding: '5px 12px', 
                  borderRadius: '5px',
                  fontSize: '13px'
                }}>
                  <strong>Actif:</strong> {getSortTypeLabel(sortType)}
                </div>
              </div>
              
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                <button onClick={() => handleSortChange("revenue")} className="sort-btn" style={{ 
                  backgroundColor: sortType === "revenue" ? '#1976d2' : '#e3f2fd', 
                  color: sortType === "revenue" ? 'white' : '#1976d2', 
                  border: '1px solid #1976d2',
                  padding: '8px 12px',
                  fontSize: '13px'
                }}>
                  💰 CA
                </button>
                <button onClick={() => handleSortChange("quantity")} className="sort-btn" style={{ 
                  backgroundColor: sortType === "quantity" ? '#4caf50' : '#e8f5e9', 
                  color: sortType === "quantity" ? 'white' : '#4caf50', 
                  border: '1px solid #4caf50',
                  padding: '8px 12px',
                  fontSize: '13px'
                }}>
                  🛒 Qté
                </button>
                <button onClick={() => handleSortChange("category")} className="sort-btn" style={{ 
                  backgroundColor: sortType === "category" ? '#ff9800' : '#fff3e0', 
                  color: sortType === "category" ? 'white' : '#ff9800', 
                  border: '1px solid #ff9800',
                  padding: '8px 12px',
                  fontSize: '13px'
                }}>
                  📂 Catégorie
                </button>
                <button onClick={() => handleSortChange("name")} className="sort-btn" style={{ 
                  backgroundColor: sortType === "name" ? '#607d8b' : '#eceff1', 
                  color: sortType === "name" ? 'white' : '#607d8b', 
                  border: '1px solid #607d8b',
                  padding: '8px 12px',
                  fontSize: '13px'
                }}>
                  🔤 Nom
                </button>
              </div>
            </div>
            
            <div>
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center', 
                marginBottom: '15px' 
              }}>
                <h4 style={{ margin: 0, color: '#6c757d', fontSize: '15px' }}>Direction:</h4>
                <div style={{ 
                  backgroundColor: sortDirection === "desc" ? '#dc3545' : '#28a745', 
                  color: 'white', 
                  padding: '5px 12px', 
                  borderRadius: '5px',
                  fontSize: '13px',
                  fontWeight: 'bold'
                }}>
                  {sortDirection === "desc" ? "↓ Descendant" : "↑ Ascendant"}
                </div>
              </div>
              
              <div style={{ display: 'flex', gap: '10px' }}>
                <button onClick={() => handleDirectionChange("desc")} className="direction-btn" style={{ 
                  backgroundColor: sortDirection === "desc" ? '#dc3545' : '#f8d7da', 
                  color: sortDirection === "desc" ? 'white' : '#dc3545', 
                  border: '1px solid #dc3545',
                  padding: '8px 15px',
                  fontSize: '13px',
                  flex: 1
                }}>
                  ↓ Descendant
                </button>
                <button onClick={() => handleDirectionChange("asc")} className="direction-btn" style={{ 
                  backgroundColor: sortDirection === "asc" ? '#28a745' : '#d4edda', 
                  color: sortDirection === "asc" ? 'white' : '#28a745', 
                  border: '1px solid #28a745',
                  padding: '8px 15px',
                  fontSize: '13px',
                  flex: 1
                }}>
                  ↑ Ascendant
                </button>
              </div>
            </div>
          </div>
          
          {/* Filtres Avancés */}
          <div className="filter-options" style={{ 
            flex: '1',
            minWidth: '300px',
            backgroundColor: 'white',
            padding: '20px',
            borderRadius: '8px',
            border: '1px solid #e0e0e0'
          }}>
            <h3 style={{ 
              color: '#495057', 
              marginBottom: '20px', 
              paddingBottom: '10px',
              borderBottom: '2px solid #28a745'
            }}>
              🔍 Filtres Avancés
            </h3>
            
            <div style={{ display: 'grid', gap: '15px', marginBottom: '20px' }}>
              {/* Date de début */}
              <div className="filter-group">
                <label htmlFor="startDate" style={{ 
                  display: 'block', 
                  marginBottom: '5px', 
                  color: '#495057',
                  fontWeight: '500',
                  fontSize: '14px'
                }}>
                  <span style={{ color: '#dc3545' }}>*</span> Date de début
                </label>
                <input
                  type="date"
                  id="startDate"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  style={{ 
                    padding: '10px', 
                    borderRadius: '5px', 
                    border: '1px solid #ced4da', 
                    width: '100%',
                    fontSize: '14px'
                  }}
                  max={endDate || new Date().toISOString().split('T')[0]}
                />
              </div>
              
              {/* Date de fin */}
              <div className="filter-group">
                <label htmlFor="endDate" style={{ 
                  display: 'block', 
                  marginBottom: '5px', 
                  color: '#495057',
                  fontWeight: '500',
                  fontSize: '14px'
                }}>
                  <span style={{ color: '#dc3545' }}>*</span> Date de fin
                </label>
                <input
                  type="date"
                  id="endDate"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  style={{ 
                    padding: '10px', 
                    borderRadius: '5px', 
                    border: '1px solid #ced4da', 
                    width: '100%',
                    fontSize: '14px'
                  }}
                  min={startDate}
                  max={new Date().toISOString().split('T')[0]}
                />
              </div>
              
              {/* Localisation (admin seulement) */}
              {currentUser?.role === "admin" && (
                <div className="filter-group">
                  <label htmlFor="location" style={{ 
                    display: 'block', 
                    marginBottom: '5px', 
                    color: '#495057',
                    fontWeight: '500',
                    fontSize: '14px'
                  }}>
                    Localisation
                  </label>
                  {loadingLocations ? (
                    <div style={{ padding: '10px', textAlign: 'center', color: '#6c757d', fontSize: '14px' }}>
                      Chargement...
                    </div>
                  ) : (
                    <select
                      id="location"
                      value={selectedLocation}
                      onChange={(e) => setSelectedLocation(e.target.value)}
                      style={{ 
                        padding: '10px', 
                        borderRadius: '5px', 
                        border: '1px solid #ced4da', 
                        width: '100%', 
                        backgroundColor: 'white',
                        fontSize: '14px'
                      }}
                    >
                      <option value="">Toutes les localisations</option>
                      {availableLocations.map((location, index) => (
                        <option key={index} value={extractLocId(location)}>
                          {location.name || location._id || `Localisation ${index + 1}`}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
              )}
              
              {/* Filtre par sous-produit */}
              <div className="filter-group">
                <label htmlFor="subProduct" style={{ 
                  display: 'block', 
                  marginBottom: '5px', 
                  color: '#495057',
                  fontWeight: '500',
                  fontSize: '14px'
                }}>
                  Sous-produit
                </label>
                {loadingSubProducts ? (
                  <div style={{ padding: '10px', textAlign: 'center', color: '#6c757d', fontSize: '14px' }}>
                    Chargement...
                  </div>
                ) : (
                  <select
                    id="subProduct"
                    value={selectedSubProduct}
                    onChange={(e) => setSelectedSubProduct(e.target.value)}
                    style={{ 
                      padding: '10px', 
                      borderRadius: '5px', 
                      border: '1px solid #ced4da', 
                      width: '100%', 
                      backgroundColor: 'white',
                      fontSize: '14px'
                    }}
                  >
                    <option value="">Tous les sous-produits</option>
                    {subProducts.map((subProduct, index) => (
                      <option key={index} value={subProduct.name}>
                        {subProduct.name}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            </div>
            
            {/* Informations de filtrage */}
            {(startDate || endDate || selectedLocation || selectedSubProduct) && (
              <div style={{
                backgroundColor: '#e9ecef',
                padding: '12px',
                borderRadius: '6px',
                marginBottom: '15px',
                borderLeft: '4px solid #17a2b8'
              }}>
                <div style={{ fontWeight: 'bold', color: '#495057', marginBottom: '8px' }}>
                  Filtres actifs:
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {startDate && (
                    <span style={{ 
                      backgroundColor: '#17a2b8', 
                      color: 'white', 
                      padding: '4px 10px', 
                      borderRadius: '4px', 
                      fontSize: '12px',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '5px'
                    }}>
                      📅 Depuis: {new Date(startDate).toLocaleDateString()}
                    </span>
                  )}
                  {endDate && (
                    <span style={{ 
                      backgroundColor: '#17a2b8', 
                      color: 'white', 
                      padding: '4px 10px', 
                      borderRadius: '4px', 
                      fontSize: '12px',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '5px'
                    }}>
                      📅 Jusqu'à: {new Date(endDate).toLocaleDateString()}
                    </span>
                  )}
                  {selectedLocation && (
                    <span style={{ 
                      backgroundColor: '#28a745', 
                      color: 'white', 
                      padding: '4px 10px', 
                      borderRadius: '4px', 
                      fontSize: '12px',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '5px'
                    }}>
                      📍 {getLocationName(selectedLocation)}
                    </span>
                  )}
                  {selectedSubProduct && (
                    <span style={{ 
                      backgroundColor: '#9c27b0', 
                      color: 'white', 
                      padding: '4px 10px', 
                      borderRadius: '4px', 
                      fontSize: '12px',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '5px'
                    }}>
                      🍔 {selectedSubProduct}
                    </span>
                  )}
                </div>
              </div>
            )}
            
            {/* Boutons d'action */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginTop: '20px' }}>
              <button onClick={handleSearch} disabled={loading} className="action-btn" style={{ 
                backgroundColor: '#28a745',
                color: 'white',
                padding: '10px 20px',
                borderRadius: '5px',
                border: 'none',
                fontSize: '14px',
                fontWeight: 'bold',
                flex: 1,
                minWidth: '120px'
              }}>
                {loading ? (
                  <>
                    <span className="spinner" style={{
                      border: '2px solid #f3f3f3',
                      borderTop: '2px solid #fff',
                      borderRadius: '50%',
                      width: '16px',
                      height: '16px',
                      animation: 'spin 1s linear infinite',
                      display: 'inline-block',
                      marginRight: '8px'
                    }}></span>
                    Analyse...
                  </>
                ) : (
                  '🔍 Analyser'
                )}
              </button>
              
              <button onClick={resetFilters} className="action-btn" style={{ 
                backgroundColor: '#6c757d',
                color: 'white',
                padding: '10px 20px',
                borderRadius: '5px',
                border: 'none',
                fontSize: '14px',
                fontWeight: 'bold',
                flex: 1,
                minWidth: '120px'
              }}>
                🗑️ Réinitialiser
              </button>
              
              <button onClick={exportToExcel} disabled={salesData.length === 0} className="action-btn" style={{ 
                backgroundColor: salesData.length > 0 ? '#17a2b8' : '#cccccc',
                color: 'white',
                padding: '10px 20px',
                borderRadius: '5px',
                border: 'none',
                fontSize: '14px',
                fontWeight: 'bold',
                flex: 1,
                minWidth: '120px'
              }}>
                📊 Exporter Excel
              </button>
            </div>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="loading" style={{ textAlign: 'center', padding: '40px' }}>
          <div className="spinner" style={{ 
            border: '4px solid #f3f3f3', 
            borderTop: '4px solid #3498db', 
            borderRadius: '50%', 
            width: '40px', 
            height: '40px', 
            animation: 'spin 2s linear infinite', 
            margin: '0 auto 20px' 
          }}></div>
          <p>Chargement et analyse des données de vente...</p>
        </div>
      ) : salesData.length === 0 ? (
        <div style={{ 
          textAlign: 'center', 
          padding: '60px 20px', 
          backgroundColor: '#f8f9fa', 
          borderRadius: '10px', 
          border: '2px dashed #dee2e6' 
        }}>
          <div style={{ fontSize: '60px', color: '#6c757d', marginBottom: '20px' }}>📊</div>
          <h3 style={{ color: '#495057', marginBottom: '10px' }}>Aucune donnée disponible</h3>
          <p style={{ color: '#6c757d', maxWidth: '600px', margin: '0 auto 20px' }}>
            {selectedSubProduct ? 
              `Aucune vente ne correspond au sous-produit "${selectedSubProduct}" dans la période sélectionnée.` :
              "Aucune vente ne correspond aux critères de filtrage sélectionnés."}
          </p>
          <button onClick={resetFilters} className="action-btn" style={{ 
            backgroundColor: '#28a745', 
            color: 'white', 
            border: 'none', 
            padding: '10px 20px', 
            borderRadius: '5px', 
            cursor: 'pointer', 
            fontSize: '16px' 
          }}>
            Voir toutes les ventes
          </button>
        </div>
      ) : (
        <>
          {/* Cartes de statistiques */}
          <div className="stats-cards" style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', 
            gap: '20px', 
            marginBottom: '30px' 
          }}>
            <div className="stat-card" style={{ 
              backgroundColor: '#e3f2fd', 
              padding: '20px', 
              borderRadius: '10px', 
              border: '2px solid #1976d2', 
              position: 'relative' 
            }}>
              <div style={{ position: 'absolute', top: '10px', right: '10px', fontSize: '24px' }}>📈</div>
              <h3 style={{ margin: '0 0 10px 0', color: '#1976d2', fontSize: '16px' }}>NOMBRE DE VENTES</h3>
              <p style={{ fontSize: '36px', fontWeight: 'bold', color: '#1976d2', margin: '10px 0' }}>{stats.totalSales}</p>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px', color: '#1976d2' }}>
                <span>Factures analysées</span>
                <span style={{ fontWeight: 'bold' }}>{allBills.length > 0 ? ((salesData.length / allBills.length) * 100).toFixed(1) : 0}% du total</span>
              </div>
            </div>
            
            <div className="stat-card" style={{ 
              backgroundColor: '#e8f5e9', 
              padding: '20px', 
              borderRadius: '10px', 
              border: '2px solid #388e3c', 
              position: 'relative' 
            }}>
              <div style={{ position: 'absolute', top: '10px', right: '10px', fontSize: '24px' }}>💰</div>
              <h3 style={{ margin: '0 0 10px 0', color: '#388e3c', fontSize: '16px' }}>CHIFFRE D'AFFAIRES</h3>
              <p style={{ fontSize: '36px', fontWeight: 'bold', color: '#388e3c', margin: '10px 0' }}>{stats.totalRevenue.toFixed(2)} DT</p>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px', color: '#388e3c' }}>
                <span>Total généré</span>
                <span style={{ fontWeight: 'bold' }}>{stats.averageSaleValue.toFixed(2)} DT moyenne</span>
              </div>
            </div>
            
            <div className="stat-card" style={{ 
              backgroundColor: '#fff3e0', 
              padding: '20px', 
              borderRadius: '10px', 
              border: '2px solid #f57c00', 
              position: 'relative' 
            }}>
              <div style={{ position: 'absolute', top: '10px', right: '10px', fontSize: '24px' }}>🛒</div>
              <h3 style={{ margin: '0 0 10px 0', color: '#f57c00', fontSize: '16px' }}>PRODUITS VENDUS</h3>
              <p style={{ fontSize: '36px', fontWeight: 'bold', color: '#f57c00', margin: '10px 0' }}>{stats.totalProductsSold}</p>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px', color: '#f57c00' }}>
                <span>Unités écoulées</span>
                <span style={{ fontWeight: 'bold' }}>{stats.totalSales > 0 ? (stats.totalProductsSold / stats.totalSales).toFixed(1) : 0} par vente</span>
              </div>
            </div>
            
            <div className="stat-card" style={{ 
              backgroundColor: '#fce4ec', 
              padding: '20px', 
              borderRadius: '10px', 
              border: '2px solid #c2185b', 
              position: 'relative' 
            }}>
              <div style={{ position: 'absolute', top: '10px', right: '10px', fontSize: '24px' }}>🏆</div>
              <h3 style={{ margin: '0 0 10px 0', color: '#c2185b', fontSize: '16px' }}>
                {sortType === "revenue" ? "MEILLEUR CA" : 
                 sortType === "quantity" ? "PLUS VENDU" : 
                 sortType === "category" ? "TOP CATÉGORIE" : 
                 "TOP PERFORMANT"}
              </h3>
              {stats.topProduct ? (
                <>
                  <p style={{ fontSize: '20px', fontWeight: 'bold', color: '#c2185b', margin: '10px 0' }}>{stats.topProduct.name}</p>
                  <div style={{ fontSize: '14px', color: '#c2185b' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
                      <span>Quantité:</span>
                      <span style={{ fontWeight: 'bold' }}>{stats.topProduct.quantity} unités</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
                      <span>CA généré:</span>
                      <span style={{ fontWeight: 'bold' }}>{stats.topProduct.revenue.toFixed(2)} DT</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span>Catégorie:</span>
                      <span style={{ 
                        backgroundColor: getCategoryColor(stats.topProduct.category).badge, 
                        color: 'white', 
                        padding: '2px 8px', 
                        borderRadius: '10px', 
                        fontSize: '12px', 
                        fontWeight: 'bold' 
                      }}>
                        {stats.topProduct.category || "Non classé"}
                      </span>
                    </div>
                  </div>
                </>
              ) : (
                <p style={{ fontSize: '18px', color: '#c2185b', textAlign: 'center', padding: '20px 0' }}>Aucun produit</p>
              )}
            </div>
          </div>

          {/* Produits les plus vendus */}
          {stats.filteredProducts && stats.filteredProducts.length > 0 && (
            <div className="top-products-section" style={{ marginBottom: '40px' }}>
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center', 
                marginBottom: '20px', 
                paddingBottom: '10px', 
                borderBottom: '2px solid #dee2e6' 
              }}>
                <h2 style={{ 
                  color: '#333', 
                  margin: 0, 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '10px' 
                }}>
                  🏆 Produits les Plus Vendus
                  <span style={{ 
                    backgroundColor: '#ffc107', 
                    color: '#212529', 
                    padding: '2px 8px', 
                    borderRadius: '12px', 
                    fontSize: '14px' 
                  }}>
                    {sortType === "revenue" ? "Par CA" : 
                     sortType === "quantity" ? "Par quantité" : 
                     sortType === "category" ? "Par catégorie" : 
                     sortType === "name" ? "Par nom" : 
                     "Par CA"}
                  </span>
                  <span style={{ 
                    backgroundColor: sortDirection === "desc" ? '#dc3545' : '#28a745', 
                    color: 'white', 
                    padding: '2px 6px', 
                    borderRadius: '12px', 
                    fontSize: '12px' 
                  }}>
                    {sortDirection === "desc" ? "↓" : "↑"}
                  </span>
                </h2>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <span style={{ 
                    backgroundColor: '#6c757d', 
                    color: 'white', 
                    padding: '5px 10px', 
                    borderRadius: '20px', 
                    fontSize: '14px' 
                  }}>
                    {stats.filteredProducts.length} produits
                  </span>
                </div>
              </div>
              
              <div className="table-responsive" style={{ 
                overflowX: 'auto', 
                border: '1px solid #dee2e6', 
                borderRadius: '5px', 
                backgroundColor: 'white' 
              }}>
                <table className="sales-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead style={{ backgroundColor: '#f8f9fa' }}>
                    <tr>
                      <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Rang</th>
                      <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Produit</th>
                      <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Catégorie</th>
                      <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Quantité Vendue</th>
                      <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Chiffre d'Affaires</th>
                      <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Factures</th>
                      <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Prix Moyen</th>
                      <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Part du CA</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.filteredProducts.slice(0, 20).map((product, index) => {
                      const marketShare = stats.totalRevenue > 0 ? (product.revenue / stats.totalRevenue) * 100 : 0;
                      const category = product.category || getCategoryFromProductName(product.name);
                      const categoryColor = getCategoryColor(category);
                      
                      return (
                        <tr key={index} style={{ 
                          backgroundColor: index % 2 === 0 ? '#fff' : '#f8f9fa', 
                          borderBottom: '1px solid #dee2e6' 
                        }}>
                          <td style={{ padding: '12px' }}>
                            <span style={{ 
                              display: 'inline-block', 
                              width: '28px', 
                              height: '28px', 
                              borderRadius: '50%', 
                              backgroundColor: index === 0 ? '#ffd700' : index === 1 ? '#c0c0c0' : index === 2 ? '#cd7f32' : '#6c757d', 
                              color: index < 3 ? '#212529' : 'white', 
                              textAlign: 'center', 
                              lineHeight: '28px', 
                              fontWeight: 'bold', 
                              fontSize: '14px' 
                            }}>
                              {index + 1}
                            </span>
                          </td>
                          <td style={{ padding: '12px', fontWeight: 'bold' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                              {product.name}
                              {index < 3 && (
                                <span style={{ 
                                  backgroundColor: index === 0 ? '#ffd700' : index === 1 ? '#c0c0c0' : '#cd7f32', 
                                  color: '#212529', 
                                  padding: '2px 6px', 
                                  borderRadius: '3px', 
                                  fontSize: '10px', 
                                  fontWeight: 'bold' 
                                }}>
                                  {index === 0 ? '🥇' : index === 1 ? '🥈' : '🥉'}
                                </span>
                              )}
                            </div>
                          </td>
                          <td style={{ padding: '12px' }}>
                            <span style={{ 
                              backgroundColor: categoryColor.badge, 
                              color: 'white', 
                              padding: '5px 10px', 
                              borderRadius: '12px', 
                              fontSize: '12px', 
                              fontWeight: 'bold', 
                              display: 'inline-block' 
                            }}>
                              {category}
                            </span>
                          </td>
                          <td style={{ padding: '12px' }}>
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                              <span style={{ fontWeight: 'bold', marginRight: '10px', minWidth: '40px' }}>{product.quantity}</span>
                              <div style={{ flexGrow: 1, height: '10px', backgroundColor: '#e9ecef', borderRadius: '5px', overflow: 'hidden' }}>
                                <div style={{ 
                                  width: `${(product.quantity / Math.max(...stats.filteredProducts.map(p => p.quantity), 1)) * 100}%`, 
                                  height: '100%', 
                                  backgroundColor: index === 0 ? '#28a745' : index === 1 ? '#20c997' : index === 2 ? '#17a2b8' : '#6c757d' 
                                }}></div>
                              </div>
                            </div>
                          </td>
                          <td style={{ padding: '12px', fontWeight: 'bold', color: '#28a745' }}>{product.revenue.toFixed(2)} DT</td>
                          <td style={{ padding: '12px' }}>{product.billsCount}</td>
                          <td style={{ padding: '12px' }}>{product.averagePrice.toFixed(2)} DT</td>
                          <td style={{ padding: '12px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                              <span style={{ fontWeight: 'bold', minWidth: '40px' }}>{marketShare.toFixed(1)}%</span>
                              <div style={{ width: '80px', height: '10px', backgroundColor: '#e9ecef', borderRadius: '5px', overflow: 'hidden' }}>
                                <div style={{ width: `${Math.min(marketShare, 100)}%`, height: '100%', backgroundColor: '#17a2b8' }}></div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              
              {/* LÉGENDE DES CATÉGORIES */}
              <div style={{ 
                marginTop: '20px', 
                padding: '15px', 
                backgroundColor: '#f8f9fa', 
                borderRadius: '8px', 
                border: '1px solid #dee2e6' 
              }}>
                <h4 style={{ marginBottom: '10px', color: '#495057' }}>Légende des catégories:</h4>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '15px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ display: 'inline-block', width: '20px', height: '20px', backgroundColor: '#4caf50', borderRadius: '4px' }}></span>
                    <span style={{ fontSize: '14px', fontWeight: 'bold', color: '#2e7d32' }}>FOOD</span>
                    <span style={{ fontSize: '12px', color: '#666' }}>Nourriture & accompagnements</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ display: 'inline-block', width: '20px', height: '20px', backgroundColor: '#2196f3', borderRadius: '4px' }}></span>
                    <span style={{ fontSize: '14px', fontWeight: 'bold', color: '#1565c0' }}>BEVERAGES</span>
                    <span style={{ fontSize: '12px', color: '#666' }}>Boissons & jus</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ display: 'inline-block', width: '20px', height: '20px', backgroundColor: '#ff9800', borderRadius: '4px' }}></span>
                    <span style={{ fontSize: '14px', fontWeight: 'bold', color: '#ef6c00' }}>DEALS</span>
                    <span style={{ fontSize: '12px', color: '#666' }}>Menus & combos</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Catégories les plus vendues */}
          {stats.filteredCategories && stats.filteredCategories.length > 0 && (
            <div className="top-categories-section" style={{ marginBottom: '40px' }}>
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center', 
                marginBottom: '20px', 
                paddingBottom: '10px', 
                borderBottom: '2px solid #dee2e6' 
              }}>
                <h2 style={{ 
                  color: '#333', 
                  margin: 0, 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '10px' 
                }}>
                  📂 Performance par Catégorie
                  <span style={{ 
                    backgroundColor: '#17a2b8', 
                    color: 'white', 
                    padding: '2px 8px', 
                    borderRadius: '12px', 
                    fontSize: '14px' 
                  }}>
                    Classement par CA
                  </span>
                </h2>
                <span style={{ 
                  backgroundColor: '#6c757d', 
                  color: 'white', 
                  padding: '5px 10px', 
                  borderRadius: '20px', 
                  fontSize: '14px' 
                }}>
                  {stats.filteredCategories.length} catégories
                </span>
              </div>
              
              <div className="categories-grid" style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', 
                gap: '20px' 
              }}>
                {stats.filteredCategories.slice(0, 3).map((category, index) => {
                  const categoryShare = stats.totalRevenue > 0 ? (category.revenue / stats.totalRevenue) * 100 : 0;
                  const categoryColor = getCategoryColor(category.name);
                  
                  return (
                    <div key={index} className="category-card" style={{ 
                      backgroundColor: categoryColor.bg, 
                      padding: '20px', 
                      borderRadius: '10px', 
                      border: `2px solid ${categoryColor.border}`, 
                      boxShadow: '0 4px 6px rgba(0,0,0,0.1)', 
                      transition: 'transform 0.3s ease' 
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                        <h3 style={{ margin: 0, color: categoryColor.text, fontSize: '18px' }}>{category.name}</h3>
                        <span style={{ 
                          backgroundColor: categoryColor.border, 
                          color: 'white', 
                          padding: '4px 10px', 
                          borderRadius: '15px', 
                          fontSize: '14px', 
                          fontWeight: 'bold' 
                        }}>
                          #{index + 1}
                        </span>
                      </div>
                      
                      <div style={{ marginBottom: '15px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                          <span style={{ color: categoryColor.text, fontWeight: '500' }}>Quantité vendue:</span>
                          <span style={{ fontWeight: 'bold', color: categoryColor.text }}>{category.quantity} unités</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                          <span style={{ color: categoryColor.text, fontWeight: '500' }}>Chiffre d'affaires:</span>
                          <span style={{ fontWeight: 'bold', color: categoryColor.text }}>{category.revenue.toFixed(2)} DT</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                          <span style={{ color: categoryColor.text, fontWeight: '500' }}>Produits distincts:</span>
                          <span style={{ fontWeight: 'bold', color: categoryColor.text }}>{category.productsCount}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                          <span style={{ color: categoryColor.text, fontWeight: '500' }}>Prix moyen:</span>
                          <span style={{ fontWeight: 'bold', color: categoryColor.text }}>{category.averagePrice.toFixed(2)} DT</span>
                        </div>
                      </div>
                      
                      <div style={{ marginTop: '20px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                          <span style={{ color: categoryColor.text, fontWeight: '500' }}>Part du marché:</span>
                          <span style={{ fontWeight: 'bold', color: categoryColor.text }}>{categoryShare.toFixed(1)}%</span>
                        </div>
                        <div style={{ height: '12px', backgroundColor: 'rgba(255,255,255,0.5)', borderRadius: '6px', overflow: 'hidden', border: '1px solid rgba(0,0,0,0.1)' }}>
                          <div style={{ width: `${Math.min(categoryShare, 100)}%`, height: '100%', backgroundColor: categoryColor.border }}></div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Informations supplémentaires */}
          <div style={{ 
            marginTop: '40px', 
            padding: '25px', 
            backgroundColor: '#f8f9fa', 
            borderRadius: '10px', 
            fontSize: '14px', 
            color: '#6c757d', 
            borderLeft: '4px solid #17a2b8' 
          }}>
            <h4 style={{ 
              color: '#495057', 
              marginBottom: '15px', 
              display: 'flex', 
              alignItems: 'center', 
              gap: '10px' 
            }}>
              <span>📝 Informations sur l'analyse</span>
            </h4>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '15px' }}>
              <div>
                <strong style={{ color: '#495057' }}>Période analysée:</strong>
                <p>{startDate ? new Date(startDate).toLocaleDateString() : "Début"} à {endDate ? new Date(endDate).toLocaleDateString() : "Aujourd'hui"}</p>
              </div>
              <div>
                <strong style={{ color: '#495057' }}>Localisation:</strong>
                <p>{getLocationName(selectedLocation)}</p>
              </div>
              <div>
                <strong style={{ color: '#495057' }}>Sous-produit:</strong>
                <p>{selectedSubProduct || "Tous les sous-produits"}</p>
              </div>
              <div>
                <strong style={{ color: '#495057' }}>Type de classement:</strong>
                <p>{getSortTypeLabel(sortType)} ({sortDirection === "desc" ? "Descendant" : "Ascendant"})</p>
              </div>
              <div>
                <strong style={{ color: '#495057' }}>Données analysées:</strong>
                <p>{salesData.length} factures sur {allBills.length} totales ({(allBills.length > 0 ? (salesData.length / allBills.length * 100).toFixed(1) : 0)}%)</p>
              </div>
              <div>
                <strong style={{ color: '#495057' }}>Produits analysés:</strong>
                <p>{stats.filteredProducts?.length || 0} produits distincts</p>
              </div>
            </div>
            <div style={{ marginTop: '15px', paddingTop: '15px', borderTop: '1px solid #dee2e6' }}>
              <strong style={{ color: '#495057' }}>Notes:</strong>
              <ul style={{ margin: '10px 0 0 20px' }}>
                <li>Les produits annulés sont exclus de l'analyse</li>
                <li>Les catégories sont déterminées selon votre structure (FOOD, BEVERAGES, DEALS)</li>
                <li>Les sous-produits héritent de la catégorie de leur produit parent</li>
                <li>Le filtrage par date inclut les factures dont la date est comprise entre les deux dates sélectionnées</li>
                <li>Le filtrage par sous-produit inclut aussi les produits principaux</li>
                <li>Les chiffres d'affaires sont en TTC</li>
              </ul>
            </div>
          </div>
        </>
      )}

      <style jsx>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        .sort-btn, .direction-btn, .action-btn {
          padding: 10px 15px;
          border-radius: 5px;
          cursor: pointer;
          font-weight: bold;
          transition: all 0.3s ease;
          border: none;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
        }
        
        .sort-btn:hover, .direction-btn:hover, .action-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 8px rgba(0,0,0,0.1);
          opacity: 0.9;
        }
        
        .sort-btn:active, .direction-btn:active, .action-btn:active {
          transform: translateY(0);
        }
        
        .category-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 8px 16px rgba(0,0,0,0.2);
        }
        
        .stat-card:hover {
          transform: translateY(-3px);
          box-shadow: 0 6px 12px rgba(0,0,0,0.15);
        }
        
        .action-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
          transform: none;
        }
        
        .spinner {
          border: 2px solid #f3f3f3;
          border-top: 2px solid #3498db;
          border-radius: 50%;
          width: 20px;
          height: 20px;
          animation: spin 1s linear infinite;
        }
        
        @media (max-width: 768px) {
          .filter-sorting-section > div {
            flex-direction: column;
          }
          
          .sorting-options, .filter-options {
            min-width: 100%;
          }
          
          .stats-cards {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </div>
  );
}

export default BestSell;